import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Alert,
    CircularProgress,
} from "@mui/material";
import {
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    OpenInNew as OpenInNewIcon,
    Description as DocumentsIcon,
} from "@mui/icons-material";
import ProfileService from "../services/Profile.service";

function getDocIcon(title) {
    const lower = (title || "").toLowerCase();
    if (lower.endsWith(".pdf")) return <PdfIcon fontSize="small" color="error" />;
    if (/\.(png|jpe?g|gif|webp)$/.test(lower))
        return <ImageIcon fontSize="small" color="primary" />;
    return <FileIcon fontSize="small" color="action" />;
}

// Personal documents on file for the logged-in user (e.g. certifications, agreements).
// Deliberately separate from project documents, which live under Projects.
export default function MyDocuments() {
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        ProfileService.getMe()
            .then(({ data }) => setDocuments(data.employee_documents || []))
            .catch(() => setError("Failed to load your documents."))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={6}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Personal documents on file for you, such as certifications and signed agreements.
                These are not project files.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && documents.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
                    <DocumentsIcon fontSize="large" color="disabled" sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        No documents on file yet.
                    </Typography>
                </Paper>
            )}

            {documents.length > 0 && (
                <Paper variant="outlined">
                    <List disablePadding>
                        {documents.map((doc, index) => (
                            <ListItem key={doc.id} disablePadding divider={index < documents.length - 1}>
                                <ListItemButton
                                    component="a"
                                    href={doc.document_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <ListItemIcon>{getDocIcon(doc.title)}</ListItemIcon>
                                    <ListItemText
                                        primary={doc.title}
                                        sx={{ minWidth: 0 }}
                                        primaryTypographyProps={{ sx: { wordBreak: "break-word" } }}
                                    />
                                    <OpenInNewIcon fontSize="small" color="action" sx={{ flexShrink: 0, ml: 1 }} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}
        </Box>
    );
}
